import { Badge, type TreeViewDataItem } from "@patternfly/react-core";
import { createElement, useMemo } from "react";
import type { IProductVersion } from "../constants/pfsContants";
import type { RedhatAffinityEntity } from "../interfaces/graphql";

interface VersionNode {
	name: string;
	id: string;
	children: ChildVersionNode[];
}

interface ChildVersionNode {
	name: string;
	id: number;
	isUnsupported: boolean;
	checkProps: {
		"aria-label": string;
		checked: boolean;
	};
}

interface AccumulatorItem {
	id: number;
	name: string;
	support_future_versions: boolean;
	shortName: string;
	versions: VersionNode[];
}

export interface TreeDataItem extends TreeViewDataItem {
	shortName?: string;
	versions?: VersionNode[];
	support_future_versions?: boolean;
}

interface TreeDataResult {
	treeData: TreeViewDataItem[];
	transformedData: TreeDataItem[];
}

const createTreeData = (
	dataList: IProductVersion[],
	dataType?: string,
	_previouslySelectedVersions?: RedhatAffinityEntity[],
	futureVersionsLabel?: string,
): TreeDataResult => {
	const transformedData: TreeDataItem[] =
		dataList?.length > 0
			? (Object.values(
					dataList?.reduce((acc: Record<number, AccumulatorItem>, elm) => {
						const [parent, child] = elm.name.split(".");
						const productId = elm?.redHatProduct?.id;

						if (!acc[productId]) {
							acc[productId] = {
								id: productId,
								name: elm?.redHatProduct?.name,
								support_future_versions: false,
								shortName: elm?.redHatProduct?.shortName,
								versions: [
									{
										name: parent,
										id: `version-${parent}`,
										children: [],
									},
								],
							};
						}
						// Check if parent version already exists in versions
						let parentVersion = acc[productId].versions.find((v) => v.name === parent);
						if (!parentVersion) {
							parentVersion = {
								name: parent,
								id: `version-${parent}`,
								children: [],
							};
							acc[productId].versions.push(parentVersion);
						}
						// Add child version under the correct parent
						if (child) {
							parentVersion.children.push({
								name: elm?.name,
								id: elm?.id,
								isUnsupported: !elm?.isSupport,
								checkProps: {
									"aria-label": dataType === "openShiftData" ? `openShift-version` : `rhel-version`,
									checked: false,
								},
							});
						}
						return acc;
					}, {}),
				) as unknown as TreeDataItem[])
			: [];
	// Sort parent versions in descending order
	const sortedVersions = transformedData[0]?.versions?.sort((a: VersionNode, b: VersionNode) => {
		const numA = parseFloat(a.name);
		const numB = parseFloat(b.name);
		return numB - numA; // Descending order
	});

	const treeData: TreeViewDataItem[] | undefined = sortedVersions?.map((elm: VersionNode) => ({
		name: dataType === "openShiftData" ? `OpenShift version ${elm?.name}` : `RHEL version ${elm?.name}`,
		id: ` Version-${elm?.id}`,
		...(elm?.children?.length > 0 && {
			children: elm?.children?.map((child: ChildVersionNode) => ({
				name: child?.isUnsupported
					? createElement(
							"span",
							{ style: { display: "flex", alignItems: "center", gap: "8px" } },
							child?.name,
							createElement(Badge, { isRead: true }, "No longer supported"),
						)
					: child?.name,
				id: String(child?.id),
				checkProps: {
					"aria-label": dataType === "openShiftData" ? `openShift-version` : `rhel-version`,
					checked: false,
				},
			})),
		}),
		checkProps: {
			"aria-label": "main-check",
			checked: false,
		},
		defaultExpanded: false,
	}));
	if (dataType === "openShiftData") {
		if (futureVersionsLabel) {
			treeData?.push({
				name: futureVersionsLabel,
				id: "1001",
				checkProps: { "aria-label": "openShift-version-future", checked: false },
				defaultExpanded: false,
			});
		}
	} else {
		treeData?.push({
			name: "All future versions of RHEL",
			id: "1002",
			checkProps: { "aria-label": "rhel-version-future", checked: false },
			defaultExpanded: false,
		});
	}

	return { treeData: treeData ?? [], transformedData };
};

export const useTreeData = (
	rhProductVersions: IProductVersion[],
	previouslySelectedVersions = [] as RedhatAffinityEntity[],
) => {
	const { OpenShiftVersionsList, RHELVersionsList, openShiftFutureVersionsMap } = useMemo(() => {
		if (!rhProductVersions)
			return {
				OpenShiftVersionsList: [],
				RHELVersionsList: [],
				openShiftFutureVersionsMap: {} as Record<string, IProductVersion[]>,
			};

		const openShiftVersions: IProductVersion[] = [];
		const rhelVersions: IProductVersion[] = [];

		// Get IDs and names of previously selected versions
		const previouslySelectedMap = new Map<number, { id: number; name: string; shortName: string }>();
		previouslySelectedVersions?.forEach((affinity) => {
			affinity?.versions?.forEach((version) => {
				previouslySelectedMap.set(version.id, {
					id: version.id,
					name: version.name,
					shortName: affinity.short_name,
				});
			});
		});

		// Track which previously selected versions we've found in the current rhProductVersions list
		const foundPreviouslySelectedKeys = new Set<string>();

		rhProductVersions.forEach((elm) => {
			const productType = elm?.redHatProduct?.shortName;
			const versionKey = `${productType}-${elm?.name}`;

			// Check if this version was previously selected by matching product type and name
			const isPreviouslySelected = Array.from(previouslySelectedMap.values()).some(
				(prev) => prev.shortName === productType && prev.name === elm?.name,
			);

			// Include if: (isPublic AND isSupport) OR isPreviouslySelected
			// Previously selected versions are included even if not public/supported anymore
			const shouldInclude = (elm?.isPublic && elm?.isSupport) || isPreviouslySelected;

			if (shouldInclude) {
				// Mark as found ONLY if we actually add it to the list
				if (isPreviouslySelected) {
					foundPreviouslySelectedKeys.add(versionKey);
				}

				if (productType === "RHOCP") {
					openShiftVersions.push(elm);
				} else if (productType === "RHEL") {
					rhelVersions.push(elm);
				}
			}
		});

		// Add previously selected versions that are not in the current rhProductVersions list
		// These are versions that are no longer available in the API response
		// Just in case the product gets deleted from the metadata API
		previouslySelectedMap.forEach((versionInfo, versionId) => {
			const versionKey = `${versionInfo.shortName}-${versionInfo.name}`;
			if (!foundPreviouslySelectedKeys.has(versionKey)) {
				// Get product info from the first item in the respective list or create a default
				const isRHOCP = versionInfo.shortName === "RHOCP";
				const targetList = isRHOCP ? openShiftVersions : rhelVersions;

				// Find product info - must match the productId of existing versions to appear in same tree
				let redHatProduct;

				// First try to find from the full rhProductVersions list to get the correct productId
				const sampleProduct = rhProductVersions.find((p) => p?.redHatProduct?.shortName === versionInfo.shortName);

				if (sampleProduct?.redHatProduct) {
					redHatProduct = sampleProduct.redHatProduct;
				} else if (targetList.length > 0) {
					redHatProduct = targetList[0].redHatProduct;
				} else {
					redHatProduct = {
						id: isRHOCP ? 2 : 1,
						name: isRHOCP ? "Red Hat OpenShift Container Platform" : "Red Hat Enterprise Linux",
						shortName: versionInfo.shortName,
						url: "",
						isPublic: false,
						isLayered: false,
					};
				}

				// Create a mock IProductVersion entry for this unsupported version
				const mockVersion: IProductVersion = {
					id: versionId,
					name: versionInfo.name,
					kernel: "",
					generalAvailabilityDate: "",
					isPublic: true,
					isSupport: false, // Mark as unsupported
					isAutoCreate: false,
					isAutoPost: false,
					productId: redHatProduct.id,
					redHatProduct: redHatProduct,
					platforms: [],
					baseVersions: [],
				};

				targetList.push(mockVersion);
			}
		});

		const allRhocpVersions = rhProductVersions.filter((elm) => elm?.redHatProduct?.shortName === "RHOCP");

		const rhocpByMajor: Record<string, IProductVersion[]> = {};
		for (const v of allRhocpVersions) {
			const major = v.name.split(".")[0];
			if (!rhocpByMajor[major]) rhocpByMajor[major] = [];
			rhocpByMajor[major].push(v);
		}

		const openShiftFutureVersionsMap: Record<string, IProductVersion[]> = {};
		for (const [major, versions] of Object.entries(rhocpByMajor)) {
			const firstGAIndex = versions.findIndex((elm) => elm.isPublic && elm.isSupport);
			if (firstGAIndex > 0) {
				const future = versions
					.slice(0, firstGAIndex)
					.filter((elm) => !elm.isPublic && !elm.isSupport)
					.slice(0, 3);
				if (future.length > 0) {
					openShiftFutureVersionsMap[major] = future;
				}
			}
		}

		const futureVersionIds = new Set(
			Object.values(openShiftFutureVersionsMap).flatMap((versions) => versions.map((v) => v.id)),
		);
		const filteredOpenShiftVersions = openShiftVersions.filter((v) => !futureVersionIds.has(v.id));

		return {
			OpenShiftVersionsList: filteredOpenShiftVersions,
			RHELVersionsList: rhelVersions,
			openShiftFutureVersionsMap,
		};
	}, [rhProductVersions, previouslySelectedVersions]);

	const openShiftFutureVersionsLabel = useMemo(() => {
		const majors = Object.keys(openShiftFutureVersionsMap);
		if (majors.length === 0) return undefined;
		const latestMajor = majors.sort((a, b) => Number(b) - Number(a))[0];
		return `Future OpenShift ${latestMajor} versions (next 3 minor versions)`;
	}, [openShiftFutureVersionsMap]);

	const openshiftTreeData = useMemo(() => {
		return createTreeData(
			OpenShiftVersionsList,
			"openShiftData",
			previouslySelectedVersions,
			openShiftFutureVersionsLabel,
		);
	}, [OpenShiftVersionsList, previouslySelectedVersions, openShiftFutureVersionsLabel]);

	const rhelTreeData = useMemo(() => {
		return createTreeData(RHELVersionsList, "rhelData", previouslySelectedVersions);
	}, [RHELVersionsList, previouslySelectedVersions]);

	return {
		OpenShiftVersionsList,
		RHELVersionsList,
		openshiftTreeData,
		rhelTreeData,
		openShiftFutureVersionsMap,
		createTreeData,
	};
};
